import { ITouch, ITracking } from '../models/member.model';

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const;
const TOUCH_KEYS = [...UTM_KEYS, 'referrer', 'landing_page', 'touched_at'] as const;

class TrackingService {
  // Precedence: campaign params on the Console URL, an external referrer, then the touch the marketing sites stored.
  public readTracking(search: string, referrer: string, cookie: string): ITracking | undefined {
    const urlParams = new URLSearchParams(search);
    const cookies = new Map<string, string>();
    for (const entry of cookie.split('; ')) {
      const index = entry.indexOf('=');
      try {
        cookies.set(entry.slice(0, index), decodeURIComponent(entry.slice(index + 1)));
      } catch {
        // a foreign cookie with malformed encoding must not break signup
      }
    }

    const touchedAt = new Date().toISOString();
    const urlTouch = this.pickTouch(key => urlParams.get(key), ['referrer']);
    const legacyTouch = this.pickTouch(key => cookies.get(key), ['referrer']);
    const last = urlTouch ? { ...urlTouch, touched_at: touchedAt }
      : referrer && !this.isInternal(referrer) ? { referrer, touched_at: touchedAt }
      : this.parseTouch(cookies.get('sb_touch')) ?? legacyTouch;
    const first = this.parseTouch(cookies.get('sb_first_touch'));

    if (!last && !first) return;
    return { ...last, first_touch: first };
  }

  private pickTouch(get: (key: string) => string | null | undefined, extraKeys: readonly (keyof ITouch)[] = []): ITouch | undefined {
    const touch: ITouch = {};
    for (const key of [...UTM_KEYS, ...extraKeys]) {
      const value = get(key);
      if (typeof value === 'string' && value.length > 0) touch[key] = value;
    }
    return Object.keys(touch).length > 0 ? touch : undefined;
  }

  private parseTouch(raw?: string): ITouch | undefined {
    if (!raw) return;
    try {
      const value = JSON.parse(raw) as Record<string, unknown>;
      const touch = this.pickTouch(key => value[key] as string | undefined, TOUCH_KEYS.slice(UTM_KEYS.length));
      if (touch?.touched_at && isNaN(Date.parse(touch.touched_at))) delete touch.touched_at;
      return touch;
    } catch {
      return;
    }
  }

  private isInternal(referrer: string): boolean {
    try {
      return new URL(referrer).hostname.endsWith('shipbook.io');
    } catch {
      return false;
    }
  }
}

export default new TrackingService();
